/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useContext, useEffect, useState } from "react";
import apiService from "services/apiService";
import DataTable, { Column, Row } from "components/DataTable";
import GenericModal from "components/Modal";
import GenericToaster from "components/Toaster";
import HeaderButtons from "components/Button/HeaderButtons";
import InventoryRequestForm from "./InventoryRequestForm";
// Asset-type filter options come from the Device Type admin config (dynamic).
import { useInventoryOptions } from "./useInventoryConfig";
import { useAppContext } from "contexts";
// import { userTableHeight } from "./InvetoriesList";
import { FaCheck, FaEdit, FaTimes, FaEye } from "react-icons/fa";
import CommonListingFilter, {
  CommonFilterField,
  CommonFilterOption,
} from "components/CommonListingFilter/CommonListingFilter";
import { InputActionMeta } from "react-select";
import { sortByKey } from "util/sortOptions";
import { BannerContext } from "components/SideBar";

export interface AssetRequest {
  id: number;
  userId: number;
  assetType: string;
  priority: string;
  reason: string;
  preferredBrand: string;
  preferredModel: string;
  status: string;
  adminComment: string | null;
  approvedBy: string | null;
  approvedAt: number | null;
  createdAt: number;
  updatedAt: number;
  userName: string;
}

interface ReviewPayload {
  status: "APPROVED" | "REJECTED";
  adminComment: string;
}

interface OptionProps {
  value: string;
  label: string;
}

type UserOptionSource = { id?: number | string; email?: string; displayName?: string };

const STATUS_FILTER_OPTIONS: CommonFilterOption[] = [
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

const PRIORITY_FILTER_OPTIONS: CommonFilterOption[] = [
  { value: "LOW", label: "Low" },
  { value: "MEDIUM", label: "Medium" },
  { value: "HIGH", label: "High" },
];

const PRIORITY_COLOR: Record<string, string> = {
  LOW: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  MEDIUM: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
  HIGH: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  CRITICAL: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

const STATUS_COLOR: Record<string, string> = {
  PENDING: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
  APPROVED: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  REJECTED: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

const Badge = ({ text, colorClass }: { text: string; colorClass: string }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${colorClass}`}>
    {text}
  </span>
);

const InventoryRequestList: React.FC<{ showMyRequests?: boolean }> = ({ showMyRequests = false }) => {
  const { userRole } = useAppContext();
  const isAdminOrManager = userRole === 2 || userRole === 3;
  // Device Type admin config feeds the "Asset Type" filter options.
  const { deviceTypeOptions } = useInventoryOptions();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [userOptions, setUserOptions] = useState<OptionProps[]>([]);
  const [userPage, setUserPage] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const USER_PAGE_SIZE = 10;

  const [filters, setFilters] = useState({
    userLabel: "",
    userId: "",
    status: "",
    priority: "",
    assetType: "",
  });

  const [draftFilters, setDraftFilters] = useState({
    userLabel: "",
    userId: "",
    status: "",
    priority: "",
    assetType: "",
  });

    const { isBannerVisible } = useContext(BannerContext);
  const dynamicHeightClass = isBannerVisible
    ? "h-[calc(100vh-146px)] min-h-[300px]"
    : "h-[calc(100vh-115px)] min-h-[300px]";

  const [commentError, setCommentError] = useState<string>("");
  const [requests, setRequests] = useState<AssetRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<AssetRequest | null>(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewPayload, setReviewPayload] = useState<ReviewPayload>({
    status: "APPROVED",
    adminComment: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [editRequest, setEditRequest] = useState<AssetRequest | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [toastData, setToastData] = useState<{
    message: string;
    severity: "info" | "error";
  } | null>(null);
  const [viewRequest, setViewRequest] = useState<AssetRequest | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  const getRequestsFromPayload = (payload: unknown): AssetRequest[] => {
    if (Array.isArray(payload)) {
      return payload as AssetRequest[];
    }

    if (
      typeof payload === "object" &&
      payload !== null &&
      "content" in payload &&
      Array.isArray((payload as { content?: unknown }).content)
    ) {
      return (payload as { content: AssetRequest[] }).content;
    }

    return [];
  };

  const getUsersFromPayload = (payload: unknown): UserOptionSource[] => {
    if (Array.isArray(payload)) {
      return payload as UserOptionSource[];
    }

    if (
      typeof payload === "object" &&
      payload !== null &&
      "content" in payload &&
      Array.isArray((payload as { content?: unknown }).content)
    ) {
      return (payload as { content: UserOptionSource[] }).content;
    }

    return [];
  };

  const fetchUsers = async (
    nextPage: number,
    searchValue: string,
    append: boolean
  ) => {
    if (loadingUsers) return;

    setLoadingUsers(true);
    try {
      const query = new URLSearchParams();
      query.set("page", String(nextPage));
      query.set("size", String(USER_PAGE_SIZE));
      if (searchValue.trim()) {
        query.set("search", searchValue.trim());
      }

      const res = await apiService.get(`api/users?${query.toString()}`);
      const payload = res.data;
      const users = getUsersFromPayload(payload);

      const fetchedOptions: OptionProps[] = users
        .filter((item) => item?.id != null)
        .map((item) => ({
          value: String(item.id),
          label: item.displayName || item.email || String(item.id),
        }));

      setUserOptions((prev) => {
        const merged = append ? [...prev, ...fetchedOptions] : fetchedOptions;
        const dedup = new Map<string, OptionProps>();
        merged.forEach((item) => {
          if (!dedup.has(item.value)) {
            dedup.set(item.value, item);
          }
        });
        return sortByKey(Array.from(dedup.values()), "label");
      });

      setUserPage(nextPage);
      if (typeof payload?.last === "boolean") {
        setHasMoreUsers(!payload.last);
      } else if (typeof payload?.totalPages === "number") {
        setHasMoreUsers(nextPage < payload.totalPages - 1);
      } else {
        setHasMoreUsers(fetchedOptions.length === USER_PAGE_SIZE);
      }
    } catch (err) {
      console.log("Failed to fetch users", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    const debounceTimer = window.setTimeout(() => {
      fetchUsers(0, userSearch, false);
    }, 350);

    return () => window.clearTimeout(debounceTimer);
  }, [userSearch]);

  const selectedUserOption =
    userOptions.find((opt) => opt.value === draftFilters.userId) ||
    (draftFilters.userId
      ? { value: draftFilters.userId, label: draftFilters.userLabel || draftFilters.userId }
      : null);

  const selectedStatusOption =
    STATUS_FILTER_OPTIONS.find((opt) => opt.value === draftFilters.status) || null;

  const selectedPriorityOption =
    PRIORITY_FILTER_OPTIONS.find((opt) => opt.value === draftFilters.priority) || null;

  const selectedAssetTypeOption =
    deviceTypeOptions.find((opt) => opt.value === draftFilters.assetType) || null;

  const handleUserSearch = (inputValue: string, meta: InputActionMeta) => {
    if (meta.action === "input-change") {
      setUserSearch(inputValue);
      return inputValue;
    }

    if (meta.action === "menu-close") {
      setUserSearch("");
      return "";
    }

    return inputValue;
  };

  const loadMoreUsers = () => {
    if (hasMoreUsers && !loadingUsers) {
      fetchUsers(userPage + 1, userSearch, true);
    }
  };

  const requestFilterFields: CommonFilterField[] = [
    {
      id: "request-user",
      type: "select",
      label: "User",
      placeholder: "All Users",
      widthClass: "min-w-[220px]",
      queryParam: "userId",
      options: userOptions as CommonFilterOption[],
      value: selectedUserOption as CommonFilterOption | null,
      isClearable: true,
      isSearchable: true,
      isLoading: loadingUsers,
      onInputChange: handleUserSearch,
      onMenuScrollToBottom: loadMoreUsers,
      noOptionsMessage: () =>
        loadingUsers ? "Loading users..." : "No users found",
      onChange: (selectedOption) =>
        setDraftFilters((prev) => ({
          ...prev,
          userId: selectedOption?.value || "",
          userLabel: selectedOption?.label || "",
        })),
    },
    {
      id: "request-status",
      type: "select",
      label: "Status",
      placeholder: "All Status",
      widthClass: "min-w-[160px]",
      queryParam: "status",
      value: selectedStatusOption,
      options: STATUS_FILTER_OPTIONS,
      isClearable: true,
      isSearchable: false,
      onChange: (option) =>
        setDraftFilters((prev) => ({ ...prev, status: option?.value || "" })),
    },
    {
      id: "request-priority",
      type: "select",
      label: "Priority",
      placeholder: "All Priority",
      widthClass: "min-w-[160px]",
      queryParam: "priority",
      value: selectedPriorityOption,
      options: PRIORITY_FILTER_OPTIONS,
      isClearable: true,
      isSearchable: false,
      onChange: (option) =>
        setDraftFilters((prev) => ({ ...prev, priority: option?.value || "" })),
    },
    {
      id: "request-asset-type",
      type: "select",
      label: "Asset Type",
      placeholder: "All Asset Type",
      widthClass: "min-w-[190px]",
      queryParam: "assetType",
      value: selectedAssetTypeOption,
      options: deviceTypeOptions,
      isClearable: true,
      isSearchable: false,
      onChange: (selectedOption: any) =>
        setDraftFilters((prev) => ({
          ...prev,
          assetType: selectedOption?.value || "",
        })),
    },
  ];

  const clearFilters = () => {
    setDraftFilters({ userId: "", status: "", priority: "", assetType: "", userLabel: "" });
  };

  const resetFilters = () => {
    const next = { userId: "", status: "", priority: "", assetType: "", userLabel: "" };
    setDraftFilters(next);
    setFilters(next);
    setPage(0);
    return false;
  };

  const applyFilters = () => {
    setFilters(draftFilters);
    setPage(0);
  };

  const fetchRequests = () => {
    setLoading(true);
    const endpoint =
      isAdminOrManager && !showMyRequests
        ? "/api/assetRequests/admin"
        : "/api/assetRequests/my";

    const query = new URLSearchParams();
    query.set("page", String(page));
    query.set("size", String(size));
    if (searchTerm.trim()) query.set("username", searchTerm.trim());
    if (filters.status) query.set("status", filters.status);
    if (filters.userId) query.set("userId", filters.userId);
    if (filters.assetType) query.set("assetType", filters.assetType);
    if (filters.priority) query.set("priority", filters.priority);

    const url = query.toString() ? `${endpoint}?${query.toString()}` : endpoint;

    apiService
      .get(url)
      .then((res) => {
        const payload = res.data;
        const requestRows = getRequestsFromPayload(payload);
        const parsedTotalElements = Number(payload?.totalElements);
        const parsedTotalPages = Number(payload?.totalPages);
        const resolvedTotalItems = Number.isFinite(parsedTotalElements)
          ? parsedTotalElements
          : Number.isFinite(parsedTotalPages)
            ? parsedTotalPages * size
            : requestRows.length;

        setRequests(requestRows);
        setTotalItems(resolvedTotalItems);
      })
      .catch(() =>
        setToastData({ message: "Failed to load requests.", severity: "error" })
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRequests();
  }, [filters, isAdminOrManager, showMyRequests, page, size, searchTerm]);

  const handleRequestPageChange = useCallback((nextPage: number) => {
    setPage(nextPage - 1);
  }, []);

  const handleRequestItemsPerPageChange = useCallback((nextSize: number) => {
    setSize(nextSize);
    setPage(0);
  }, []);

  const handleRequestSearchTermChange = useCallback((nextSearchTerm: string) => {
    setSearchTerm((prevSearchTerm) => {
      if (prevSearchTerm === nextSearchTerm) {
        return prevSearchTerm;
      }

      setPage(0);
      return nextSearchTerm;
    });
  }, []);

  const handleReviewOpen = (row: AssetRequest, action: "APPROVED" | "REJECTED") => {
    setSelectedRequest(row);
    setReviewPayload({ status: action, adminComment: "" });
    setCommentError("");
    setReviewModalOpen(true);
  };

  const handleReviewSubmit = () => {
    if (!selectedRequest) return;
    if (!reviewPayload.adminComment.trim()) {
      // setToastData({ message: "Please add a comment before submitting.", severity: "error" });
      if (!reviewPayload.adminComment.trim()) {
        setCommentError("Please add a comment before submitting.");
        return;
      }
      return;
    }
    setSubmitting(true);
    apiService
      .put(`/api/assetRequests/${selectedRequest.id}`, reviewPayload)
      .then(() => {
        setToastData({
          message: `Request ${reviewPayload.status.toLowerCase()} successfully!`,
          severity: "info",
        });
        setReviewModalOpen(false);
        fetchRequests();
      })
      .catch(() =>
        setToastData({ message: "Failed to update request.", severity: "error" })
      )
      .finally(() => setSubmitting(false));
  };

  const adminColumns: Column[] = [
    // { key: "id", label: "ID" },
    { key: "userName", label: "User Name", bold: true },
    { key: "assetType", label: "Asset Type", bold: true },
    {
      key: "priority",
      label: "Priority",
      render: (val) => (
        <Badge text={val as string} colorClass={PRIORITY_COLOR[val as string] ?? ""} />
      ),
    },
    { key: "reason", label: "Description" },

    {
      key: "status",
      label: "Status",
      render: (val) => (
        <Badge text={val as string} colorClass={STATUS_COLOR[val as string] ?? ""} />
      ),
    },
    {
      key: "actions",
      label: "Actions",
      excludeExport: true,
      actions: (row: Row) => {
        const req = row as unknown as AssetRequest;
        // if (req.status !== "PENDING") return <span className="text-xs text-gray-400">—</span>;
        return (
          <div className="flex flex-start">
            {/* View Button - Always visible */}
            <span
              onClick={() => { setViewRequest(req); setViewModalOpen(true); }}
              className="btn-primary px-2 py-2 mr-2 rounded-lg"
            >
              <FaEye />
            </span>
            {req.status === "PENDING" ? (
              <>
                <span
                  onClick={() => handleReviewOpen(req, "APPROVED")}
                  className="btn-primary px-2 py-2 mr-2 rounded-lg"
                >
                  <FaCheck />
                </span>
                <span
                  onClick={() => handleReviewOpen(req, "REJECTED")}
                  className="btn-red px-2 py-2 mr-2 rounded-lg"
                >
                  <FaTimes />
                </span>
              </>
            ) : (
              <span className="text-xs text-gray-400 px-2">—</span>
            )}
          </div>
        );
      },
    },
  ];

  const userColumns: Column[] = [
    // { key: "id", label: "ID" },
    { key: "userName", label: "User Name", bold: true },
    { key: "assetType", label: "Asset Type", bold: true },
    {
      key: "priority",
      label: "Priority",
      render: (val) => (
        <Badge text={val as string} colorClass={PRIORITY_COLOR[val as string] ?? ""} />
      ),
    },
    { key: "reason", label: "Description" },

    {
      key: "status",
      label: "Status",
      render: (val) => (
        <Badge text={val as string} colorClass={STATUS_COLOR[val as string] ?? ""} />
      ),
    },
    {
      key: "adminComment",
      label: "Admin Comment",
      render: (val) => <span className="text-gray-500 dark:text-gray-400">{val as string || "—"}</span>,
    },
    {
      key: "actions",
      label: "Actions",
      excludeExport: true,
      actions: (row: Row) => {
        const req = row as unknown as AssetRequest;
        if (req.status !== "PENDING") return <span className="text-xs text-gray-400">—</span>;
        return (
          <div className="flex flex-start">
            {/* View Button - Always visible */}
            <span
              onClick={() => { setViewRequest(req); setViewModalOpen(true); }}
              className="btn-primary px-2 py-2 mr-2 rounded-lg"
            >
              <FaEye />
            </span>
            <span
              onClick={() => { setEditRequest(req); setEditModalOpen(true); }}
              className="btn-primary px-2 py-2 mr-2 rounded-lg"
            >
              <FaEdit />
            </span>
          </div>
        );
      },
    },
  ];

  const tableData: Row[] = requests.map((r) => ({ ...r, id: r.id }));

  return (
    <div className="flex flex-col text-slate-800 dark:text-slate-200">
      <DataTable
        data={tableData}
        columns={isAdminOrManager && !showMyRequests ? adminColumns : userColumns}
        loading={loading}
        itemsPerPage={size}
        serverSidePagination
        currentPage={page + 1}
        totalItems={totalItems}
        onPageChange={handleRequestPageChange}
        onItemsPerPageChange={handleRequestItemsPerPageChange}
        onSearchTermChange={handleRequestSearchTermChange}
        showSearch={true}
        searchPlaceholder="Search"
        heightClass={dynamicHeightClass}
        leftSideButtons={
          <CommonListingFilter
            fields={requestFilterFields}
            panelTitle="Request Filter"
            onApply={applyFilters}
            onClear={clearFilters}
            onReset={resetFilters}
            disableApplyOnEmpty={true}
          />
        }
      // title={isAdminOrManager && !showMyRequests ? "All Asset Requests" : "My Asset Requests"}
      // heightClass={userTableHeight}
      />
      {/* View Request Modal */}
      {viewModalOpen && viewRequest && (
        <GenericModal
          isOpen={viewModalOpen}
          onClose={() => setViewModalOpen(false)}
          header={`View Request`}
          customWidth="620px"
        >
          <InventoryRequestForm
            viewMode={true}
            viewData={{
              userName: viewRequest.userName,
              assetType: viewRequest.assetType,
              priority: viewRequest.priority,
              reason: viewRequest.reason,
              preferredBrand: viewRequest.preferredBrand,
              preferredModel: viewRequest.preferredModel,
              status: viewRequest.status,
              adminComment: viewRequest.adminComment,
              approvedBy: viewRequest.approvedBy,
              approvedAt: viewRequest.approvedAt,
              createdAt: viewRequest.createdAt,
            }}
            onClose={() => setViewModalOpen(false)}
          />
        </GenericModal>
      )}

      {/* Edit Request Modal — user PENDING requests */}
      {editModalOpen && editRequest && (
        <GenericModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          header={`Edit Request #${editRequest.id}`}
          customWidth="620px"
        >
          <InventoryRequestForm
            requestId={editRequest.id}
            initialData={{
              assetType: editRequest.assetType,
              priority: editRequest.priority,
              reason: editRequest.reason,
              preferredBrand: editRequest.preferredBrand,
              preferredModel: editRequest.preferredModel,
            }}
            onClose={() => setEditModalOpen(false)}
            onSuccess={(toastPayload) => {
              setEditModalOpen(false);
              fetchRequests();
              setToastData(toastPayload);
            }}
          />
        </GenericModal>
      )}

      {/* Approve / Reject Review Modal */}
      {reviewModalOpen && selectedRequest && (
        <GenericModal
          isOpen={reviewModalOpen}
          onClose={() => setReviewModalOpen(false)}
          header={`${reviewPayload.status === "APPROVED" ? "Approve" : "Reject"} Request #${selectedRequest.id}`}
          customWidth="520px"
        >
          <div className="flex flex-col gap-4 p-1">
            {/* Request summary */}
            <div className="rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 flex flex-col gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Asset Type</span>
                <span className="font-semibold">{selectedRequest.assetType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Priority</span>
                <Badge
                  text={selectedRequest.priority}
                  colorClass={PRIORITY_COLOR[selectedRequest.priority] ?? ""}
                />
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Reason</span>
                <span className="max-w-xs text-right">{selectedRequest.reason}</span>
              </div>
              {selectedRequest.preferredBrand && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Brand</span>
                  <span>{selectedRequest.preferredBrand}</span>
                </div>
              )}
              {selectedRequest.preferredModel && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Model</span>
                  <span>{selectedRequest.preferredModel}</span>
                </div>
              )}
            </div>

            {/* Comment */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Comment <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={3}
                value={reviewPayload.adminComment}
                onChange={(e) => {
                  const value = e.target.value;
                  setReviewPayload((p) => ({ ...p, adminComment: value }));

                  // Real-time validation check
                  if (!value.trim()) {
                    setCommentError("Please add a comment before submitting.");
                  } else {
                    setCommentError("");
                  }
                }}
                placeholder={`Add a comment for ${reviewPayload.status.toLowerCase()}...`}
                className={`w-full rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 resize-none ${commentError
                  ? "border border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                  : "border border-gray-300 dark:border-gray-600 focus:ring-primary focus:border-primary"
                  }`}
              />
              {commentError && (
                <span className="text-xs font-medium text-red-500 dark:text-red-400 mt-0.5">
                  {commentError}
                </span>
              )}
            </div>

            {/* Footer actions */}
            <div className="flex justify-end gap-2 pt-1">
              <HeaderButtons
                type="button"
                variant="inactive"
                onClick={() => setReviewModalOpen(false)}
                disabled={submitting}
              >
                Cancel
              </HeaderButtons>
              <HeaderButtons
                type="button"
                variant="primary"
                onClick={handleReviewSubmit}
                disabled={submitting}
              >
                {submitting
                  ? "Submitting..."
                  : reviewPayload.status === "APPROVED"
                    ? "Confirm Approve"
                    : "Confirm Reject"}
              </HeaderButtons>
            </div>
          </div>
        </GenericModal>
      )}

      {toastData && (
        <GenericToaster
          open={true}
          message={toastData.message}
          severity={toastData.severity}
          onClose={() => setToastData(null)}
        />
      )}
    </div>
  );
};

export default InventoryRequestList;
